'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import SearchInput from '@/app/[locale]/blog/_components/SearchInput'
import AppLink from '@/components/elementary/AppLink'
import { GridEntries } from '@/components/shared/components/GridEntries'
import { MobileCategoryNavigation } from '@/components/shared/components/MobileCategoryNavigation'
import { SidePanelCategoryNavigation } from '@/components/shared/components/SidePanelCategoryNavigation'
import Heading from '@/components/typography/Heading'
import { Paragraph } from '@/components/typography/Paragraph'
import { ROOT_PAGE_PATH } from '@/lib/constants'
import { usePathname } from '@/lib/navigation'
import type { GridCardProps, GridCategoryNavigationData, GridTypeProps } from '@/types/general'
import type { ServerRenderedEntry } from '@/types/helpers'

export const GridWithCategoryFilters = ({
	entries,
	categories,
	type,
	additionalLink,
}: {
	readonly categories: GridCategoryNavigationData
	readonly entries?: ServerRenderedEntry<GridCardProps>[] | null
	readonly type: GridTypeProps
	readonly additionalLink?: Data.Component<'utilities.link'> | null
}) => {
	const t = useTranslations('grids')
	const pathname = usePathname()
	const { allCategories, topCategories } = categories

	// Normalizes the current pathname by removing the root page path and any trailing slash.
	const normalizedPathname = pathname.replace(ROOT_PAGE_PATH, '').replace(/\/$/, '')

	const currentCategory = allCategories.find(
		(category) => category?.fullPath?.replace(ROOT_PAGE_PATH, '') === normalizedPathname,
	)

	const allEntries =
		entries?.filter(
			(entry) =>
				entry?.categories?.some(
					(category) => category?.documentId === currentCategory?.documentId,
				) ||
				topCategories.some(
					(category) => category?.fullPath?.replace(ROOT_PAGE_PATH, '') === normalizedPathname,
				),
		) ?? []

	const [filteredEntries, setFilteredEntries] = useState(allEntries)

	const searchItems = (searchString: string) => {
		if (!searchString) {
			setFilteredEntries(allEntries)
			return
		}

		const lowerCaseSearchString = searchString.toLowerCase()
		const filtered = allEntries.filter((entry) =>
			entry.title?.toLowerCase().includes(lowerCaseSearchString),
		)
		setFilteredEntries(filtered)
	}

	const featuredEntries = filteredEntries.filter((entry) => entry?.isFeatured)
	const restEntries = filteredEntries.filter((entry) => !entry?.isFeatured)

	return (
		<div className="flex lg:flex-row flex-col gap-8 justify-between">
			{Array.isArray(categories?.allCategories) && categories.allCategories.length > 0 && (
				<MobileCategoryNavigation {...categories} triggerLabel={t(`allLabel.${type}`)} />
			)}

			<SidePanelCategoryNavigation {...categories} additionalLink={additionalLink} />

			<div className="flex flex-1 flex-col">
				{(type === 'partners' || type === 'apps') && (
					<SearchInput className="mb-6" onInputChange={searchItems} customOnSearch={searchItems} />
				)}

				{filteredEntries.length === 0 && <Paragraph className="mb-6">{t('noResults')}</Paragraph>}

				<div className="flex flex-col gap-7.5">
					{type === 'partners' && (
						<Heading tag="h2" fontSize="displaySmall">
							{currentCategory?.title}
						</Heading>
					)}

					{type === 'apps' && featuredEntries.length > 0 ? (
						<Heading tag="h2" fontSize="displaySmall">
							{t('featuredApps')}
						</Heading>
					) : null}

					{featuredEntries.length > 0 && <GridEntries entries={featuredEntries} type={type} />}

					{type === 'apps' && restEntries.length > 0 ? (
						<Heading tag="h2" fontSize="displaySmall">
							{t('allApps')}
						</Heading>
					) : null}

					{type === 'library' && (
						<Heading tag="h2" fontSize="displaySmall" className="sr-only">
							{currentCategory?.title}
						</Heading>
					)}
					{restEntries.length > 0 && <GridEntries entries={restEntries} type={type} />}
				</div>
			</div>

			{additionalLink?.href && (
				<div className="border-t border-neutral-light-40 pt-9 pb-9 lg:hidden">
					<AppLink
						href={additionalLink.href}
						openExternalInNewTab={additionalLink.newTab ?? false}
						size="none"
						variant="ghost"
					>
						{additionalLink.label}
					</AppLink>
				</div>
			)}
		</div>
	)
}
